<template>
  <section
    v-if="desktop && 0 !== contentful.underFilterSlider.length"
    class="aside-slider"
  >
    <div class="aside-slider__slider">
      <div class="aside-slider__slides">
        <div
          v-for="(slide, index) in contentful.underFilterSlider"
          :key="index"
          class="aside-slider__slide slide"
        >
          <NuxtLink
            v-if="slide.link"
            :to="`${lang.urlPrefix}/${slide.link}`"
            @click="scrollTop"
          >
            <img
              class="slide__img-wrap"
              :src="useNormalizeCtfUrl(slide.img.file.url)"
              :alt="slide.img.title"
            />
          </NuxtLink>
          <div class="slide__content">
            <h3 v-if="slide.title" class="slide__title">
              {{ getLocalizedName(slide.title) }}
            </h3>
            <p class="slide__description">
              {{ getLocalizedName(slide.description)?.content[0].content[0].value  }}
            </p>
            <NuxtLink
              v-if="slide.link"
              class="slide__button"
              :to="`${lang.urlPrefix}/${slide.link}`"
              @click="scrollTop"
            >
              {{ _T("@More details") }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
    <div class="aside-slider__pagination">
      <ul class="aside-slider__nav">
        <li
          v-for="(dot, index) in contentful.underFilterSlider"
          :key="index"
          class="aside-slider__nav-item"
          :class="{ active: index === counter }"
          @click="activePagination(index)"
        />
      </ul>
    </div>
  </section>
</template>

<script setup>
import { useContentfulStore } from "~/store/contentfulStore";
import { addScopedEventListener } from "~/utils/eventListener";
import { useLanguageFilterStore } from "~/store/languageFilterStore";
import { useNormalizeCtfUrl } from "~/utils/normalize";

const contentful = useContentfulStore();

const languageStore = useLanguageFilterStore();
const { getUserLanguage: lang } = storeToRefs(languageStore);

const counter = ref(null);
const sliderLength = ref(0);
const translateX = ref(0);
const translateXVar = ref("");

const interval = ref(-1);

await contentful.fetchUnderFilterSlider();

const desktop = ref(null);

function getSliderValues() {
  desktop.value = window.innerWidth > 1024;

  if (desktop.value && contentful.underFilterSlider.length) {
    sliderLength.value = contentful.underFilterSlider.length;
    counter.value = 0;

    translateX.value = 100 * counter.value;
    translateXVar.value = `-${translateX.value}%`;
  }
}

function nextSlide() {
  if (counter.value < sliderLength.value - 1) {
    counter.value++;
    translateX.value = 100 * counter.value;
  } else {
    counter.value = 0;
    translateX.value = 0;
  }

  translateXVar.value = `-${translateX.value}%`;
}

function activePagination(index) {
  counter.value = index;
  translateX.value = 100 * counter.value;

  translateXVar.value = `-${translateX.value}%`;
}

function scrollTop() {
  window.scrollTo(0, 0);
}

function endInterval(interval) {
  clearInterval(interval);
}

watch(counter, () => {
  endInterval(interval.value);
  interval.value = setInterval(nextSlide, 5000);
});

onMounted(() => {
  getSliderValues();
  addScopedEventListener(window, "resize", getSliderValues);
});
</script>

<style lang="scss" scoped>
.aside-slider {
  position: relative;

  background-image: url("/img/slide_bg.png");
  background-size: cover;

  border: 1px solid #e9e9e9;
  border-radius: 8px;

  overflow: hidden;

  &::after {
    content: "";
    width: 320px;
    height: 324px;

    position: absolute;
    z-index: 2;
    top: -100px;
    left: -32px;

    border-radius: 50%;
    background-color: #fff;
  }

  &__slides {
    position: relative;
    z-index: 10;

    @include flex-container(row, flex-start, center);

    transform: translateX(v-bind(translateXVar));

    transition: transform 0.5s ease-in-out;
  }

  &__pagination {
    position: absolute;
    z-index: 10;
    bottom: 8px;
    left: 50%;
    transform: translate(-50%);
  }

  &__nav {
    @include flex-container(row, flex-start, center);
    gap: 8px;
  }

  &__nav-item {
    width: 8px;
    height: 8px;

    background-color: transparent;
    border: 1px solid white;
    border-radius: 50%;

    cursor: pointer;

    transition: all 0.5s ease-in-out;

    &.active {
      width: 20px;

      background-color: white;
      border-radius: 10px;
    }
  }
}

.slide {
  flex: 0 0 100%;

  position: relative;
  text-align: center;

  padding-bottom: 32px;

  &__img-wrap {
    height: 204px;

    padding: 0 18px;
    margin-bottom: 35px;
  }

  &__content {
    @include flex-container(column, center);
    gap: 8px;

    color: white;

    padding: 0 16px;
  }

  &__title {
    @include font(16, 23, 600);
    letter-spacing: 0.32px;
  }

  &__description {
    @include font(14, 20, 400);
    letter-spacing: 0.28px;

    margin-bottom: 8px;
  }

  &__button {
    @include font(18, 25, 400);
    letter-spacing: 0.36px;
    color: var(--color-primary-base);

    border-radius: 6px;
    background-color: #fff;

    padding: 8px;
    margin: 0 15px;

    &:hover {
      color: var(--color-primary-light);
    }
  }
}
</style>